/**
 * EditBranchModal - Modal for editing default branch
 *
 * Features:
 * - Select default branch from available branches
 * - Load branches from repository
 * - Save changes
 */

import { createPortal } from 'react-dom';
import { X, Loader2 } from 'lucide-react';
import { Dropdown } from '../common';

interface EditBranchModalProps {
  isOpen: boolean;
  repoFullName: string;
  branches: { name: string }[];
  selectedBranch: string;
  isLoadingBranches: boolean;
  isSaving: boolean;
  hasChanges: boolean;
  onClose: () => void;
  onSelectBranch: (branch: string) => void;
  onSave: () => void;
}

export function EditBranchModal({
  isOpen,
  repoFullName,
  branches,
  selectedBranch,
  isLoadingBranches,
  isSaving,
  hasChanges,
  onClose,
  onSelectBranch,
  onSave,
}: EditBranchModalProps) {
  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl max-w-lg w-full">
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            Edit Default Branch
          </h3>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-4">
          <div className="mb-4">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Repository: <span className="font-medium text-gray-900 dark:text-white">{repoFullName}</span>
            </p>
          </div>
          <div>
            <label
              htmlFor="defaultBranch"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
            >
              Default Branch
            </label>
            {isLoadingBranches ? (
              <div className="flex items-center gap-2 p-3 border border-gray-300 dark:border-gray-600 rounded-lg">
                <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
                <span className="text-sm text-gray-600 dark:text-gray-400">Loading branches...</span>
              </div>
            ) : (
              <Dropdown
                options={branches.map((b) => ({ label: b.name, value: b.name }))}
                value={selectedBranch}
                onChange={onSelectBranch}
                emptyMessage="No branches found for this repository"
              />
            )}
          </div>
        </div>
        <div className="flex items-center justify-end gap-3 p-4 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            disabled={isSaving}
          >
            Cancel
          </button>
          <button
            onClick={onSave}
            disabled={isSaving || isLoadingBranches || !hasChanges}
            className={`px-4 py-2 rounded-lg transition-colors flex items-center ${
              !isSaving && !isLoadingBranches && hasChanges
                ? 'bg-indigo-600 hover:bg-indigo-700 text-white'
                : 'bg-gray-300 dark:bg-gray-600 text-gray-500 cursor-not-allowed'
            }`}
          >
            {isSaving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {isSaving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
